import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles, Zap, MapPin,
    Clock, RotateCcw, CheckSquare
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FRECUENCIAS = ['Diaria', 'Cada 2 días', 'Semanal', 'Cuando la resistencia aparezca'];

const CHECKLIST_OPTIONS = [
    'Materiales preparados la noche anterior',
    'Notificaciones silenciadas',
    'Espacio despejado',
    'Dispositivos innecesarios fuera de alcance',
    'Agua/café listo',
    'Persona informada de mi compromiso'
];

export default function Modulo35DAI() {
    const [tareaFaro, setTareaFaro] = useState('');
    const [primeraAccion, setPrimeraAccion] = useState('');
    const [hora, setHora] = useState('');
    const [lugar, setLugar] = useState('');
    const [reglaSiEntonces, setReglaSiEntonces] = useState('');
    const [checklistFriccion, setChecklistFriccion] = useState([]);
    const [criterioCompletado, setCriterioCompletado] = useState('');
    const [frecuencia, setFrecuencia] = useState('Diaria');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [step, setStep] = useState(1);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                // Load lighthouse task from profile
                const profileRes = await axios.get(`${API_URL}/api/user/profile`, { withCredentials: true });
                if (profileRes.data.lighthouse_task) setTareaFaro(profileRes.data.lighthouse_task);

                // Load draft
                const draftRes = await axios.get(`${API_URL}/api/user/draft/dai`, { withCredentials: true });
                if (draftRes.data?.data) {
                    const d = draftRes.data.data;
                    if (d.tareaFaro) setTareaFaro(d.tareaFaro);
                    if (d.primeraAccion) setPrimeraAccion(d.primeraAccion);
                    if (d.hora) setHora(d.hora);
                    if (d.lugar) setLugar(d.lugar);
                    if (d.reglaSiEntonces) setReglaSiEntonces(d.reglaSiEntonces);
                    if (d.checklistFriccion) setChecklistFriccion(d.checklistFriccion);
                    if (d.criterioCompletado) setCriterioCompletado(d.criterioCompletado);
                    if (d.frecuencia) setFrecuencia(d.frecuencia);
                }
                if (draftRes.data.read_only) { setReadOnly(true); setSubmittedAt(draftRes.data.submitted_at); }
            } catch { /* silent */ } finally { setLoadingData(false); }
        };
        loadData();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(`${API_URL}/api/user/draft/save`, {
                module_id: 'dai',
                data: { tareaFaro, primeraAccion, hora, lugar, reglaSiEntonces, checklistFriccion, criterioCompletado, frecuencia }
            }, { withCredentials: true });
        } catch { /* silent */ } finally { setSavingDraft(false); }
    }, [tareaFaro, primeraAccion, hora, lugar, reglaSiEntonces, checklistFriccion, criterioCompletado, frecuencia]);

    useEffect(() => {
        if (loadingData || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [tareaFaro, primeraAccion, hora, lugar, reglaSiEntonces, checklistFriccion, criterioCompletado, frecuencia, saveDraft, loadingData, readOnly]);

    const toggleChecklist = (item) => {
        setChecklistFriccion(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
    };

    const canSubmit = !readOnly && primeraAccion.trim().length > 0 && hora.trim() && lugar.trim() && 
                      reglaSiEntonces.trim().length > 0 && criterioCompletado.trim().length > 0;

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) { toast.error('Completa todos los campos requeridos'); return; }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/dai/submit`, {
                tarea_faro: tareaFaro, primera_accion: primeraAccion, hora, lugar,
                regla_si_entonces: reglaSiEntonces, checklist_friccion: checklistFriccion,
                criterio_completado: criterioCompletado, frecuencia
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('¡Capítulo 3 completado!');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingData) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-2xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl">
                    <CardContent className="pt-8">
                        <div className="text-center mb-8">
                            <div className="w-24 h-24 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                                <Zap className="h-12 w-12 text-white" />
                            </div>
                            <h2 className="text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Acción inevitable lista</h2>
                            <p className="text-[#5FAF9D] font-medium">Capítulo 3 finalizado</p>
                        </div>
                        {/* DAI Card */}
                        <div className="bg-gradient-to-br from-[#F5F7F8] to-white rounded-xl border border-[#DCE3E8] p-6 mb-6 space-y-4">
                            <div className="p-3 bg-gradient-to-r from-[#5FAF9D]/10 to-transparent rounded-lg border border-[#5FAF9D]/20">
                                <p className="text-xs text-[#5FAF9D] uppercase font-semibold mb-1">Tarea faro</p>
                                <p className="text-[#1F2A30] font-medium">{tareaFaro}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 bg-white rounded-lg border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1"><Zap className="h-4 w-4 text-[#E8B27A]" /><p className="text-xs text-[#7A8A93] uppercase">Primera acción</p></div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{primeraAccion}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1"><Clock className="h-4 w-4 text-[#3E5F6E]" /><p className="text-xs text-[#7A8A93] uppercase">Hora</p></div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{hora}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1"><MapPin className="h-4 w-4 text-[#5FAF9D]" /><p className="text-xs text-[#7A8A93] uppercase">Lugar</p></div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{lugar}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg border border-[#DCE3E8]">
                                    <div className="flex items-center gap-2 mb-1"><RotateCcw className="h-4 w-4 text-[#7A9BA8]" /><p className="text-xs text-[#7A8A93] uppercase">Frecuencia</p></div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{frecuencia}</p>
                                </div>
                            </div>
                            <div className="p-3 bg-[#3E5F6E]/5 rounded-lg">
                                <p className="text-xs text-[#3E5F6E] uppercase font-semibold mb-1">Regla si-entonces</p>
                                <p className="text-sm text-[#1F2A30]">{reglaSiEntonces}</p>
                            </div>
                        </div>
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl mb-6">
                                <div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic">"{aiInsight}"</p></div>
                            </div>
                        )}
                        <div className="flex justify-center">
                            <div className="mb-6"><ModuleFeedback moduleId="dai" chapter={3} /></div>
                            <Link to="/usuario"><Button className="bg-gradient-to-r from-[#3E5F6E] to-[#2F4A57] text-white px-10 py-4 rounded-xl shadow-lg text-lg">Volver al inicio<ArrowRight className="h-5 w-5 ml-2" /></Button></Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 3</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 3.5</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Acción inevitable (DAI)</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#5FAF9D] to-[#4A9A89] p-8 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                <Zap className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-white">Diseña tu primera acción</CardTitle>
                                <p className="text-sm text-white/70">Convierte tu tarea faro en un paso concreto con alta probabilidad de inicio</p>
                            </div>
                        </div>
                        {tareaFaro && (
                            <div className="mt-4 p-3 bg-white/10 rounded-xl">
                                <p className="text-xs text-white/60 uppercase font-semibold">Tu tarea faro</p>
                                <p className="text-white font-medium">{tareaFaro}</p>
                            </div>
                        )}
                    </CardHeader>
                    <CardContent className="p-8 space-y-6">
                        {/* Primera acción */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2">
                                <Zap className="h-4 w-4 text-[#E8B27A]" />
                                Primera acción física concreta (max 20 min)
                            </Label>
                            <Textarea value={primeraAccion} onChange={e => setPrimeraAccion(e.target.value)}
                                placeholder="Ej: Abrir el documento y escribir las primeras 3 líneas del resumen"
                                className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                data-testid="primera-accion-input" />
                        </div>

                        {/* Hora y Lugar */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-[#3E5F6E]" /> Hora
                                </Label>
                                <input type="time" value={hora} onChange={e => setHora(e.target.value)}
                                    className="w-full px-4 py-3 border border-[#DCE3E8] rounded-xl bg-[#F5F7F8] focus:ring-1 focus:ring-[#5FAF9D] outline-none text-[#1F2A30]"
                                    data-testid="hora-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-[#5FAF9D]" /> Lugar
                                </Label>
                                <input type="text" value={lugar} onChange={e => setLugar(e.target.value)}
                                    placeholder="Ej: Mi escritorio, la biblioteca..."
                                    className="w-full px-4 py-3 border border-[#DCE3E8] rounded-xl bg-[#F5F7F8] focus:ring-1 focus:ring-[#5FAF9D] outline-none text-[#1F2A30]"
                                    data-testid="lugar-input" />
                            </div>
                        </div>

                        {/* Regla si-entonces */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Regla si-entonces</Label>
                            <Textarea value={reglaSiEntonces} onChange={e => setReglaSiEntonces(e.target.value)}
                                placeholder="Ej: Si son las 7:00 AM y estoy en mi escritorio, entonces abro el documento y escribo 3 líneas"
                                className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                data-testid="regla-input" />
                        </div>

                        {/* Checklist de fricción */}
                        <div className="space-y-3">
                            <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2">
                                <CheckSquare className="h-4 w-4" /> Eliminación de fricción
                            </Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {CHECKLIST_OPTIONS.map(opt => (
                                    <button key={opt} onClick={() => toggleChecklist(opt)}
                                        className={`flex items-center gap-3 p-3 rounded-xl text-left text-sm transition-all ${checklistFriccion.includes(opt) ? 'bg-[#5FAF9D]/10 border border-[#5FAF9D]/30 text-[#1F2A30]' : 'bg-[#F5F7F8] border border-[#DCE3E8] text-[#7A8A93]'}`}>
                                        <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${checklistFriccion.includes(opt) ? 'bg-[#5FAF9D] text-white' : 'border border-[#DCE3E8]'}`}>
                                            {checklistFriccion.includes(opt) && <Check className="h-3 w-3" />}
                                        </div>
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Criterio de completado */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">¿Cómo sabrás que completaste la acción?</Label>
                            <input type="text" value={criterioCompletado} onChange={e => setCriterioCompletado(e.target.value)}
                                placeholder="Ej: 3 líneas escritas en el documento"
                                className="w-full px-4 py-3 border border-[#DCE3E8] rounded-xl bg-[#F5F7F8] focus:ring-1 focus:ring-[#5FAF9D] outline-none text-[#1F2A30]"
                                data-testid="criterio-input" />
                        </div>

                        {/* Frecuencia */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2">
                                <RotateCcw className="h-4 w-4" /> Frecuencia
                            </Label>
                            <div className="flex flex-wrap gap-2">
                                {FRECUENCIAS.map(f => (
                                    <button key={f} onClick={() => setFrecuencia(f)}
                                        className={`px-4 py-2 rounded-xl text-sm transition-all ${frecuencia === f ? 'bg-[#3E5F6E] text-white shadow-md' : 'bg-[#F5F7F8] text-[#7A8A93] border border-[#DCE3E8]'}`}>
                                        {f}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading}
                                className="bg-gradient-to-r from-[#5FAF9D] to-[#4A9A89] text-white px-10 py-4 rounded-xl shadow-lg text-lg"
                                data-testid="submit-dai">
                                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Procesando...</> : <><Check className="h-5 w-5 mr-2" />Completar Capítulo 3</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
